import { useState, useEffect, useRef } from "react";
import { IoIosLogOut } from "react-icons/io";
import { IoSettingsOutline, IoMailOutline, IoBusinessOutline } from "react-icons/io5";
import "./ProfileMenu.css";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AuthApi } from "../../../../api/common/apiIndex";
import { useAuth } from "../../../../context/AuthProvider";

function ProfileMenue({ profileMenuOpen, setProfileMenuOpen, basePath }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout, memberships, activeBusinessId, activeMembership, switchBusiness } = useAuth();
  const [busy, setBusy] = useState(false);
  const [switchingBusinessId, setSwitchingBusinessId] = useState(null);
  const menuRef = useRef(null);

  const firstName = user?.firstName?.trim() || "";
  const lastName  = user?.lastName?.trim()  || "";
  const email     = user?.email || "";
  const displayName = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : "Guest";
  const initials = `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "U";

  useEffect(() => {
    if (!profileMenuOpen) return;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setProfileMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    menuRef.current?.focus();

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [profileMenuOpen, setProfileMenuOpen]);

  const handleLogOut = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await AuthApi.logout().catch(() => {});
    } finally {
      await queryClient.cancelQueries({ predicate: () => true });
      queryClient.clear();
      toast.dismiss();
      logout();
      navigate("/login", { replace: true });
      setBusy(false);
    }
  };

  const handleSettings = () => {
    setProfileMenuOpen(false);
    navigate(`${basePath}/settings`);
  };

  const handleBusinessChange = async (event) => {
    const nextBusinessId = event.target.value;
    if (!nextBusinessId || String(nextBusinessId) === String(activeBusinessId) || switchingBusinessId) return;
    const parsedBusinessId = Number(nextBusinessId);

    setSwitchingBusinessId(nextBusinessId);
    try {
      await switchBusiness(Number.isNaN(parsedBusinessId) ? nextBusinessId : parsedBusinessId);
      await queryClient.cancelQueries({ predicate: () => true });
      queryClient.clear();
      toast.success("Business switched.");
      setProfileMenuOpen(false);
      navigate("/home", { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Unable to switch business.";
      toast.error(msg);
    } finally {
      setSwitchingBusinessId(null);
    }
  };

  const canSwitchBusiness = Array.isArray(memberships) && memberships.length > 1;

  if (!profileMenuOpen) return null;

  return (
    <div
      ref={menuRef}
      tabIndex={-1}
      className="profile-menu dropdown-menu dropdown-menu-end show shadow-lg border-0 p-0"
      role="menu"
      aria-label="Profile menu"
      data-bs-popper="static"
    >
      <div className="px-3 py-3 d-flex align-items-center gap-3 border-bottom">
        <div className="profile-avatar fw-bold">{initials}</div>
        <div className="min-w-0">
          <div className="fw-semibold text-truncate">{displayName}</div>
          <div className="small text-body-secondary d-flex align-items-center gap-2 text-truncate">
            <IoMailOutline aria-hidden="true" /> <span className="text-truncate">{email || "—"}</span>
          </div>
        </div>
      </div>

      {canSwitchBusiness && (
        <div className="px-3 py-3 border-bottom">
          <label className="small text-body-secondary d-flex align-items-center gap-2 mb-2" htmlFor="business-switcher">
            <IoBusinessOutline aria-hidden="true" /> Business
          </label>
          <select
            id="business-switcher"
            className="form-select form-select-sm"
            value={activeBusinessId ?? ""}
            onChange={handleBusinessChange}
            disabled={!!switchingBusinessId}
          >
            {memberships.map((membership) => (
              <option key={membership.businessId} value={membership.businessId}>
                {membership.businessName || `Business ${membership.businessId}`}
              </option>
            ))}
          </select>
          {switchingBusinessId && (
            <div className="small text-body-secondary mt-2">Switching business…</div>
          )}
        </div>
      )}

      {!canSwitchBusiness && activeMembership?.businessName && (
        <div className="px-3 py-2 border-bottom small text-body-secondary d-flex align-items-center gap-2">
          <IoBusinessOutline aria-hidden="true" />
          <span className="text-truncate">{activeMembership.businessName}</span>
        </div>
      )}

      <button
        type="button"
        className="dropdown-item d-flex align-items-center gap-2 py-2"
        onClick={handleSettings}
      >
        <IoSettingsOutline size={18} /> Settings
      </button>

      <div className="dropdown-divider m-0" />

      <button
        type="button"
        className="dropdown-item d-flex align-items-center gap-2 py-2 text-danger"
        onClick={handleLogOut}
        disabled={busy}
      >
        {busy && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />}
        <IoIosLogOut size={18} /> Log out
      </button>
    </div>
  );
}

export default ProfileMenue;
